import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Send, Info, ShieldCheck } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { COMPANY } from "@/config/company";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { pageTitle } from "@/lib/seo";
import { useSiteContent, scValue } from "@/hooks/useSiteContent";

const newSid = () => (crypto?.randomUUID ? crypto.randomUUID() : `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const Contact = () => {
  const { t, lang } = useI18n();
  const sc = useSiteContent();
  const pc = sc?.page_contact;
  const cv = (base, tkey) => scValue(pc, base, lang, t(tkey));
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", subject: "", message: "", privacy: false });
  const [sid, setSid] = useState(newSid());
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const emailOk = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

  const validate = () => {
    const e = {};
    const fn = form.first_name.trim(), ln = form.last_name.trim(), su = form.subject.trim(), ms = form.message.trim();
    if (fn.length < 2 || fn.length > 60) e.first_name = t("contact.errName");
    if (ln.length < 2 || ln.length > 60) e.last_name = t("contact.errName");
    if (!emailOk(form.email.trim())) e.email = t("contact.errEmail");
    if (su.length < 3 || su.length > 120) e.subject = t("contact.errSubject");
    else if (/[\r\n]/.test(form.subject)) e.subject = t("contact.errSubjectInvalid");
    if (ms.length < 10 || ms.length > 5000) e.message = t("contact.errMessage");
    if (!form.privacy) e.privacy = t("contact.errPrivacy");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return; // non azzera il form
    setSending(true);
    try {
      await api.post("/contact", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        privacy_consent: form.privacy,
        language: lang,
        submission_id: sid,
      });
      toast.success(t("contact.success"));
      setForm({ first_name: "", last_name: "", email: "", subject: "", message: "", privacy: false });
      setErrors({});
      setSid(newSid());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo title={pageTitle(cv("title", "contact.title"))} description={cv("subtitle", "contact.subtitle")} path="/contatti" type="website" />
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading tracking-tight">{cv("title", "contact.title")}</h1>
          <p className="mt-4 text-white/70 max-w-2xl">{cv("subtitle", "contact.subtitle")}</p>
        </div>
      </section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
        <aside data-testid="contact-aside" className="lg:col-span-2 lg:order-2 rounded-2xl border border-brand-electric/20 bg-gradient-to-br from-brand-electric/[0.07] to-brand-orange/[0.05] p-6 sm:p-7">
          <div className="flex items-center gap-2 text-brand-electric">
            <div className="h-10 w-10 rounded-xl bg-brand-electric/15 flex items-center justify-center"><Info className="h-5 w-5" /></div>
            <h3 className="font-heading font-semibold text-lg text-brand-navy">{cv("asideTitle", "contact.asideTitle")}</h3>
          </div>
          <p className="mt-4 text-sm text-brand-navy/70 leading-relaxed whitespace-pre-line">{cv("asideBody", "contact.asideBody")}</p>
          <div className="mt-6 pt-5 border-t border-brand-navy/10 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Mail className="h-4 w-4 text-brand-electric shrink-0" />
              <a href={`mailto:${COMPANY.supportEmail}`} className="text-brand-navy/70 hover:text-brand-electric break-all">{COMPANY.supportEmail}</a>
            </div>
            <div className="flex items-center gap-3 text-sm text-brand-navy/70">
              <MapPin className="h-4 w-4 text-brand-orange shrink-0" /> {t("footer.tagline")}
            </div>
          </div>
        </aside>
        <form onSubmit={submit} data-testid="contact-form" noValidate className="lg:col-span-3 lg:order-1 rounded-2xl border border-brand-navy/10 bg-white p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-first">{t("contact.firstName")}</Label>
              <Input id="c-first" data-testid="contact-first-name" maxLength={60} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5" />
              {errors.first_name && <p data-testid="err-first-name" className="text-xs text-red-600 mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <Label htmlFor="c-last">{t("contact.lastName")}</Label>
              <Input id="c-last" data-testid="contact-last-name" maxLength={60} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" />
              {errors.last_name && <p data-testid="err-last-name" className="text-xs text-red-600 mt-1">{errors.last_name}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-email">{t("contact.email")}</Label>
              <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
              {errors.email && <p data-testid="err-email" className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label htmlFor="c-subject">{t("contact.subject")}</Label>
              <Input id="c-subject" data-testid="contact-subject" maxLength={120} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" />
              {errors.subject && <p data-testid="err-subject" className="text-xs text-red-600 mt-1">{errors.subject}</p>}
            </div>
          </div>
          <div>
            <Label htmlFor="c-msg">{t("contact.message")}</Label>
            <Textarea id="c-msg" data-testid="contact-message" rows={5} maxLength={5000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
            {errors.message && <p data-testid="err-message" className="text-xs text-red-600 mt-1">{errors.message}</p>}
          </div>
          <div>
            <div className="flex items-start gap-2.5">
              <Checkbox id="c-privacy" data-testid="contact-privacy" checked={form.privacy} onCheckedChange={(v) => setForm({ ...form, privacy: !!v })} className="mt-0.5" />
              <Label htmlFor="c-privacy" className="text-sm font-normal text-brand-navy/70 leading-snug cursor-pointer">
                {t("contact.privacyConsent")}{" "}
                <Link to="/privacy" target="_blank" className="text-brand-electric hover:underline font-medium">{t("legal.privacy")}</Link>
              </Label>
            </div>
            {errors.privacy && <p data-testid="err-privacy" className="text-xs text-red-600 mt-1">{errors.privacy}</p>}
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={sending}
            className="w-full rounded-full h-11 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
            <Send className="h-4 w-4" /> {t("contact.send")}
          </Button>
          <p className="text-xs text-brand-navy/50 leading-relaxed" data-testid="contact-privacy-note">
            {t("contact.sendNoticeA")}{" "}
            <Link to="/privacy" target="_blank" className="text-brand-electric hover:underline font-medium">{t("legal.privacy")}</Link>
            {t("contact.sendNoticeB")}
          </p>
        </form>
      </div>
    </div>
  );
};

export default Contact;
